import {
  FluidDisplayDensity,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
} from '@stencil/core';

import { CONTEXT_BAR_ENTRY } from '../../../constants/constants';
import { ElementRegistry } from '../../../utils/elements/element-registry.utils';
import { Utils } from '../../../utils/utils';
import {
  ContextBarBody,
  ContextBarPanel,
} from './components/fluid-page-context-bar.components';

/**
 * @displayName Page Context Bar
 *
 * @contextData FLUID Page Context Bar is a versatile component designed to provide supplemental contextual information related to the current page or section of an application. It is displayed below FLUID Header and remains "sticky", staying visible as the user scrolls down the page.
 * <br/><br/>
 * The component is divided into two sections: the "body" and the "context panel". The "body" section is always visible, and should be used for important information that should be displayed upfront. The "context panel" is an optional expandable panel that can be toggled open or closed by the user. The "context panel" is intended to display additional information that is not critical to the user's primary task, but may be useful for reference.
 * <br/><br/>
 * <strong>Page Context Bar MUST be implemented as a child of FLUID Page. It is not intended to be used as a standalone component.</strong> When implemented as a DIRECT child of FLUID Page, the component does not require a slot attribute. However, if Page Context Bar is implemented as a child of another component/element (e.g. within a micro-UI), its top-most parent that is a direct child of FLUID Page must have the attribute <code>slot="page-context-bar"</code>.
 *
 *
 * @slot - Content displayed "upfront" in the component's body
 * @slot context-panel - Named slot for content displayed in optional expandable panel (below component's body)
 */
@Component({
  tag: 'fluid-page-context-bar',
  styleUrl: 'fluid-page-context-bar.scss',
  shadow: true,
})
export class FluidPageContextBar
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to the MutationObserver that monitors Context Bar for
   * changes in slotted child content.
   */
  private _slotObserver: MutationObserver;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidPageContextBarElement;

  // ================================================================ //
  // -- States

  /**
   * Whether the "context panel" is open (false) or closed (true).
   */
  @State() _panelCollapsed = true;

  /**
   * True if "context-panel" slot is found on child element(s), false
   * if not. Controls whether expandable panel & toggle Button are rendered.
   */
  @State() _hasPanelSlot = false;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * When set to "compact", removes min-height & all top/bottom padding
   * from Page Context Bar.
   */
  @Prop({ mutable: true }) displayDensity: FluidDisplayDensity =
    FluidDisplayDensity.DEFAULT;

  // ================================================================ //
  // -- Events

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad(): Promise<void> | void {
    this.detectSlotContent();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    ElementRegistry.addElement(CONTEXT_BAR_ENTRY, this.host);
  }

  /**
   * Called every time the component is disconnected from the DOM.
   */
  disconnectedCallback() {
    ElementRegistry.removeElement(CONTEXT_BAR_ENTRY, this.host);
    this._slotObserver?.disconnect();
    this._slotObserver = undefined;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Determines if Context Bar has slotted "context-panel" content, then
   * initializes MutationObserver that monitors root element for changes
   * in slotted content. If slotted child(ren) exist(s), Context Bar will
   * dynamically render the "context panel" section of the component.
   */
  detectSlotContent(): void {
    this._hasPanelSlot = Utils.hasSlotContent(this.host, 'context-panel');

    if (isNullOrUndefined(this._slotObserver)) {
      this._slotObserver = Utils.watchChildContent(
        this.host,
        Utils.debounce(() => this.detectSlotContent(), 100),
        {
          attributes: true,
          attributeFilter: ['slot'],
          childList: true,
          subtree: true,
        }
      );
    }
  }

  /**
   * If slotted "context panel" content exists, opens/closes the
   * expandable bottom panel.
   */
  togglePanel(collapsed = !this._panelCollapsed) {
    this._panelCollapsed = collapsed;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <aside
        class={{
          [`fluid-page-context-bar theme-${this.theme}`]: true,
          [`display-density-${this.displayDensity}`]: true,
        }}>
        <ContextBarBody
          togglePanel={() => this.togglePanel()}
          panelCollapsed={this._panelCollapsed}
          hasPanelSlot={this._hasPanelSlot}
          theme={this.theme}
        />
        <ContextBarPanel
          hasPanelSlot={this._hasPanelSlot}
          panelCollapsed={this._panelCollapsed}
        />
      </aside>
    );
  }
}
